import React, { useEffect } from 'react';
import { mediosDeVenta } from '../../../utils/mediosVentaCantina';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const fechaHora = (value) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

export default function VentaDetalleModal({
  abierto,
  venta,
  cargando,
  onClose,
  onAnular,
}) {
  useEffect(() => {
    if (!abierto) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [abierto, onClose]);

  if (!abierto) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-azul-oscuro/80 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-blanco-puro/20 bg-azul-oscuro shadow-2xl">
        <div className="flex items-start justify-between border-b border-blanco-puro/10 px-6 py-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amarillo-dorado">
              Cantina
            </div>

            <h2 className="mt-1 text-2xl font-bold text-blanco-puro">
              {venta ? `Venta #${venta.id}` : 'Detalle de venta'}
            </h2>

            {venta && (
              <div className="mt-1 text-sm text-blanco-puro/60">
                {fechaHora(venta.created_at)}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-blanco-puro/15 text-xl text-blanco-puro/70 transition hover:bg-blanco-puro/10 hover:text-blanco-puro"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="max-h-[calc(90vh-90px)] overflow-y-auto p-6">
          {cargando && (
            <div className="py-12 text-center text-sm text-blanco-puro/60">
              Cargando detalle...
            </div>
          )}

          {!cargando && venta && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                    Vendedor
                  </div>
                  <div className="mt-1 font-bold text-blanco-puro">
                    {venta.usuario?.nombre || '—'}
                  </div>
                </div>

                <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                    Medio de pago
                  </div>
                  <div className="mt-1 font-bold text-blanco-puro">
                    {mediosDeVenta(venta).length > 1
                      ? mediosDeVenta(venta)
                          .map(
                            (medio) =>
                              `${medio.nombre} ${money(medio.monto)}`
                          )
                          .join(' + ')
                      : venta.medioPago?.nombre || '—'}
                  </div>
                </div>

                <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                    Estado
                  </div>
                  <div
                className={`mt-1 font-bold ${
                  venta.estado === 'ANULADA'
                    ? 'text-red-300'
                    : 'text-verde-esmeralda'
                }`}
              >
                {venta.estado || '—'}
              </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-blanco-puro/50">
                  Productos
                </div>

                <div className="overflow-hidden rounded-2xl border border-blanco-puro/10">
                  <div className="divide-y divide-blanco-puro/10">
                    {(venta.items || []).map((item) => (
                      <div
                        key={item.id}
                        className="grid grid-cols-[1fr_auto] gap-4 bg-blanco-puro/[0.03] p-4"
                      >
                        <div>
                          <div className="font-bold text-blanco-puro">
                            {item.producto?.nombre || 'Producto'}
                          </div>

                          <div className="mt-1 text-xs text-blanco-puro/50">
                            {Number(item.cantidad || 0)} × {money(item.precio_unitario)}
                          </div>

                          {(item.producto?.codigo ||
                            item.producto?.codigo_barras) && (
                            <div className="mt-1 text-[11px] text-blanco-puro/40">
                              {item.producto?.codigo
                                ? `Código: ${item.producto.codigo}`
                                : ''}
                              {item.producto?.codigo &&
                              item.producto?.codigo_barras
                                ? ' · '
                                : ''}
                              {item.producto?.codigo_barras
                                ? `Barras: ${item.producto.codigo_barras}`
                                : ''}
                            </div>
                          )}
                        </div>

                        <div className="self-center text-right font-bold text-blanco-puro">
                          {money(item.subtotal)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between rounded-2xl bg-amarillo-dorado px-5 py-4 text-azul-oscuro">
                <span className="text-sm font-bold uppercase tracking-wide">
                  Total
                </span>

                <span className="text-2xl font-black">
                  {money(venta.total)}
                </span>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {venta.estado === 'COMPLETADA' && (
                <button
                  type="button"
                  onClick={() => onAnular?.(venta)}
                  className="rounded-xl border border-red-400/40 bg-red-500/10 px-5 py-2.5 text-sm font-bold text-red-200 transition hover:bg-red-500/20"
                >
                  Anular venta
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-blanco-puro/20 px-5 py-2.5 text-sm font-bold text-blanco-puro transition hover:bg-blanco-puro/10"
              >
                Cerrar
              </button>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
